/**
 * One icon per downloadable file type, so every download control in the app names its formats the
 * same way.
 *
 * Worth having in one place: the format pickers (device export, billing catalog, sensor data, test
 * reports) are on unrelated screens, and an operator who learns that the red box means PDF on one of
 * them should not meet a different icon on the next. The names are Material Design Icons, loaded from
 * `@mdi/font@latest` in `public/index.html`.
 *
 * **Deliberately import-free.** Nearly every screen with a download uses it, and `@/utils/Utils`
 * would drag the Vuex store, the router and firebase in behind it. The `ExportFormat` mapping lives
 * in `DeviceExportFormats.ts` for the same reason: the enum belongs to the module that acts on it.
 */

/** A downloadable file type, independent of which screen offers it. */
export enum DownloadFileType {
  PDF = "PDF",
  JSON = "JSON",
  CSV = "CSV",
  EXCEL = "EXCEL",
  IMAGE = "IMAGE",
  ARCHIVE = "ARCHIVE",
  TEXT = "TEXT",
  /** A machine-readable description (an OpenAPI file, #2610090). */
  YAML = "YAML",
  /** A page to open in a browser (#2610090). */
  HTML = "HTML",
}

const FILE_TYPE_ICONS: Record<DownloadFileType, string> = {
  [DownloadFileType.PDF]: "mdi-file-pdf-box",
  [DownloadFileType.JSON]: "mdi-code-json",
  [DownloadFileType.CSV]: "mdi-file-delimited-outline",
  [DownloadFileType.EXCEL]: "mdi-file-excel-box",
  [DownloadFileType.IMAGE]: "mdi-file-image-outline",
  [DownloadFileType.ARCHIVE]: "mdi-folder-zip-outline",
  [DownloadFileType.TEXT]: "mdi-file-document-outline",
  [DownloadFileType.YAML]: "mdi-file-code-outline",
  [DownloadFileType.HTML]: "mdi-language-html5",
};

/** Shown when the type is unknown, or when a control downloads whatever a record happens to hold. */
export const UNKNOWN_FILE_TYPE_ICON = "mdi-file-outline";

/** The action icon itself, for the button that performs the download. */
export const DOWNLOAD_ICON = "mdi-download";

/** Icon for a file type. */
export function getFileTypeIcon(fileType: DownloadFileType | null | undefined): string {
  return fileType ? FILE_TYPE_ICONS[fileType] : UNKNOWN_FILE_TYPE_ICON;
}

/**
 * Label of a CSV option, carrying the separator the file actually uses.
 *
 * Every CSV IRES writes is **semicolon**-separated, and so is the Beschreibung the ERP mirror
 * produces. That matters to whoever opens the file: a comma-configured Excel puts a semicolon file
 * into a single column. Material Design Icons has one delimited-file glyph and no comma/semicolon
 * variants, so the separator is stated in the label rather than drawn.
 */
export function getCsvFormatLabel(separator: string): string {
  return `CSV (${separator})`;
}

/**
 * Icon for a file name or a storage path, for controls that download a stored file rather than
 * rendering one.
 *
 * Matched on the extension only, and lower-cased first: stored report names are not normalised, and
 * a `.PDF` from an ERP mirror is the same thing as a `.pdf`.
 */
export function getFileTypeIconForFileName(fileName: string | null | undefined): string {
  const extension = (fileName ?? "").toLowerCase().split(".").pop() ?? "";

  switch (extension) {
    case "pdf":
      return getFileTypeIcon(DownloadFileType.PDF);
    case "json":
      return getFileTypeIcon(DownloadFileType.JSON);
    case "csv":
      return getFileTypeIcon(DownloadFileType.CSV);
    case "xlsx":
    case "xls":
      return getFileTypeIcon(DownloadFileType.EXCEL);
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
      return getFileTypeIcon(DownloadFileType.IMAGE);
    case "zip":
      return getFileTypeIcon(DownloadFileType.ARCHIVE);
    case "txt":
      return getFileTypeIcon(DownloadFileType.TEXT);
    case "yaml":
    case "yml":
      return getFileTypeIcon(DownloadFileType.YAML);
    case "html":
    case "htm":
      return getFileTypeIcon(DownloadFileType.HTML);
    default:
      return UNKNOWN_FILE_TYPE_ICON;
  }
}
